export const MotionDuration = {
  instantMs: 0,
  quickMs: 200,
  mediumMs: 400,
  longMs: 800,
} as const;

export const MotionEasing = {
  inOut: [0.5, 0, 0.5, 1],
  /** Expo-out. Travels most of the distance immediately, then settles — what
      makes a surface read as arriving rather than sliding. */
  expoOut: [0.16, 1, 0.3, 1],
  inOutHeavy: [0.8, 0, 0.2, 1],
  /** Accelerate-only. For things leaving, which owe no settle. */
  in: [0.4, 0, 1, 1],
  /**
   * The `--ease-out-strong` curve as Framer Motion wants it. Everything below
   * that needs this curve is derived from these four numbers rather than
   * writing them out again, so the CSS token and the JS one cannot drift.
   */
  outStrong: [0.22, 1, 0.36, 1],
} as const;

const cssCubicBezier = (ease: readonly number[]): string =>
  `cubic-bezier(${ease.join(", ")})`;

/** The tables below are in milliseconds; Framer Motion wants seconds. */
export const seconds = (ms: number): number => ms / 1000;

export const MOTION_EASE_IN_OUT = cssCubicBezier(MotionEasing.inOut);
export const MOTION_EASE_IN_OUT_HEAVY = cssCubicBezier(MotionEasing.inOutHeavy);
export const MOTION_EASE_OUT = cssCubicBezier(MotionEasing.outStrong);
export const MOTION_EASE_IN = cssCubicBezier(MotionEasing.in);

/**
 * The press every control shares. Figma draws On Click with the same colours
 * as Hover throughout the CTAs frame, so scale is the only thing that reads as
 * pressed — faster than the colour settle, and immediate.
 *
 * The caller says which properties it transitions and turns transitions off
 * under reduced motion; this owns the press and its reduced-motion opt-out.
 */
export const PRESS_SCALE_CLASSES = [
  "active:scale-[0.97] active:delay-0 active:[transition-duration:90ms]",
  "motion-reduce:active:scale-100",
].join(" ");

/**
 * The hover both banner-shaped links wear: the image scales up behind a card
 * box that holds still. It lives here because the blog teasers and the
 * cross-link banner are the same gesture, and two copies of it drift.
 */
export const HOVER_IMAGE_ZOOM_CLASSES = [
  "transition-transform duration-300 ease-out-strong group-hover:scale-[1.03]",
  "motion-reduce:transition-none motion-reduce:group-hover:scale-100",
].join(" ");

/**
 * The chrome's tone change, for the overlaid announcement bar and nav. Their
 * colours come from tokens that `data-theme` swaps wholesale, so without a
 * transition the wordmark and the announcement rule snap between black and
 * white the instant the page under them changes.
 *
 * Matched to the media under the chrome: a chrome that settles slower than
 * the media it sits on leaves white text on a white product still for the
 * difference.
 */
export const CHROME_THEME_FADE_CLASSES = [
  "transition-colors duration-quick ease-in-out",
  "motion-reduce:transition-none",
].join(" ");

/**
 * The sub-desktop nav's motion, and the row-to-pill handoff at every width.
 *
 * These pieces — the sheet, the content inside it, the glyph on the control
 * that opens it — only read as one object because they share these numbers. A
 * duration tuned in one and not the others is what turns a morph back into a
 * crossfade, so they live here rather than next to whichever component uses
 * them first.
 *
 * The desktop hover panel and floating surface are NOT here: they still open
 * on `MotionTransition.navMorph`. Moving them onto this table would change how
 * they open, which is a design decision rather than a refactor.
 */
export const NavMotion = {
  /**
   * The sub-desktop sheet arriving. It is already the right width and shape on
   * frame 0 and only unfolds vertically.
   */
  sheetOpen: { durationMs: 300, ease: MotionEasing.expoOut },
  /** How far the sheet is folded up before it opens. */
  sheetCollapsedScaleY: 0.96,
  /** Collapsing is faster than opening — the viewer has already moved on. */
  surfaceClose: { durationMs: 170, ease: MotionEasing.outStrong },
  /** Content arriving inside a surface that has already started growing. */
  contentIn: { durationMs: 160, ease: MotionEasing.outStrong },
  /** How far each item rises as it arrives. */
  contentLiftPx: 6,
  /** Content leaving, ahead of the surface it sits in. */
  contentOut: { durationMs: 90, ease: MotionEasing.in },
  /**
   * The menu control's own mark changing. Starts with the surface, at t=0, and
   * is done well inside it: the mark has to have changed before the sheet has
   * finished making room, or the control reads as lagging the thing it opened.
   */
  glyphSwapMs: 120,
  /** Between adjacent items, nearest the trigger first. */
  staggerMs: 20,
  /** Content waits this long after the surface starts before any of it lands. */
  contentStartMs: 100,

  /**
   * Swapping content inside an already-open surface, on the axis the surface
   * did NOT grow along — vertical growth switches horizontally and horizontal
   * growth switches vertically. Moving along the growth axis would read as the
   * surface still opening; moving across it reads as going somewhere.
   *
   * The incoming layer is held back so the two are never both legible: the
   * outgoing one is most of the way gone before the other starts.
   */
  viewSwitch: {
    outDurationMs: 140,
    inDurationMs: 200,
    /**
     * 90, not the 60 the two durations suggest. The outgoing layer is on an
     * ease-in, so it is still above 40% at ~101ms, while the incoming one is
     * on a strong ease-out and clears 40% about 24ms after it starts — at 60ms
     * that overlaps for two or three frames with both legible at once, which
     * is the overlapping-text failure this whole sequence exists to avoid.
     */
    inDelayMs: 90,
    /** Along the cross axis; sign is the travel direction. */
    travelPx: 24,
  },

  /**
   * The in-flow row handing over to the floating pill.
   *
   * Distance, not duration: the pill has to be fully there on the frame the
   * row's bottom edge crosses the viewport top, and a timed fade cannot
   * promise that — scroll fast enough and the row is gone before a 220ms
   * transition is half done, which is the ~17-frame hole this replaces. Mapped
   * to scroll position instead, the two are in lockstep at any speed and the
   * reverse needs no hysteresis, because position is monotonic where a
   * threshold is not.
   */
  handoff: {
    /** Scroll distance before the crossing over which the pill arrives. */
    distancePx: 80,
    /** How far the pill rises into place, so it lands rather than appears. */
    liftPx: 16,
    /**
     * Finish this far before the crossing rather than exactly on it. A scroll
     * listener runs after the scroll that triggered it, and on a device with
     * momentum that can be a frame or two — enough for the row to leave with
     * the pill still short of opaque. Arriving early costs nothing visually
     * and makes the overlap impossible to lose.
     */
    leadPx: 12,
  },
} as const;

export const MotionTransition = {
  buyRide: {
    durationMs: 2200,
    ease: MotionEasing.inOut,
  },
  navHover: {
    durationMs: 350,
    ease: MotionEasing.outStrong,
    cssEase: MOTION_EASE_OUT,
  },
  navMorph: {
    durationMs: 200,
    ease: [0, 0, 0.2, 1],
  },
} as const;

/**
 * The desktop Shop / Resources hover dropdown.
 *
 * Scaled from a mobile side-drawer reference: that drawer opens over ~850ms on
 * an ease-out-expo glide with rows staggering in behind it, and closes faster
 * on an accelerate with no stagger and no fade. The character carries over; the
 * timings do not — a hover surface already costs the user 120ms of open delay,
 * so 850ms would read as lag rather than glide.
 *
 * Deliberately separate from `MotionTransition.navMorph`, which the floating
 * pill, the menu card and the menu glyph share.
 */
export const NAV_PANEL_MOTION = {
  /** Box: height expansion + opacity. Grows from its top edge — the panel is
   *  absolutely positioned by `top`, so height is the only axis that moves. */
  openMs: 270,
  /** ease-out-expo: ~75% of the travel in the first fifth, no overshoot. */
  openEase: MotionEasing.expoOut,
  closeMs: 170,
  /** A decelerate on the way out too. The reference accelerates away, but on a
   *  hover surface an exit that starts slow delays the one frame the user is
   *  watching; this keeps the box moving from the first frame and puts the
   *  slack at the end, where it reads as the panel settling shut. */
  closeEase: [0.4, 0, 0.2, 1],
  /** Rows follow the box out rather than racing it. */
  contentExitMs: 110,
  /** Content starts once the box is clearly underway. */
  contentDelayMs: 70,
  contentStaggerMs: 24,
  contentEnterMs: 260,
  contentRisePx: 6,
  /** Tab swap inside an already-open panel: no stagger, a shorter rise. */
  crossfadeMs: 120,
  crossfadeRisePx: 4,
} as const;

/** The trigger label shares the panel's curve, but not its duration: it rides
 *  MotionDuration.quickMs so it lands with the highlight behind it. */
export const NAV_PANEL_TRIGGER_EASE_CSS = "cubic-bezier(0.16, 1, 0.3, 1)";

export type NavMorphConfig = {
  openMs: number;
  closeMs: number;
  openEase: string;
  closeEase: string;
  fadeMs: number;
  delayMs: number;
};

export const NAV_OPEN_DELAY_MS = 120;
export const NAV_DISMISS_DELAY_MS = 120;

// Snappy ease-out: the panel arrives on a decelerate and dismisses faster than
// it opens, with the content fading a touch ahead of the box morph.
export const NAV_MORPH_DEFAULT: NavMorphConfig = {
  openMs: 240,
  closeMs: 160,
  openEase: MOTION_EASE_OUT,
  closeEase: MOTION_EASE_IN,
  fadeMs: 140,
  delayMs: NAV_OPEN_DELAY_MS,
};
